<script setup lang="ts">
import { useForm } from 'vee-validate'
import * as yup from 'yup'

const { t, locale } = useI18n()

const localePath = useLocalePath()

const route = useRoute()

const { data: countries } = useFetchCountries(locale.value)
const { data: stature } = useFetchHeightOptions(locale.value)

// Define error
const errorAlert = useState('error')

// Define form state
const formState = useState('formState')

const savedFormState = computed(() => formState.value ? formState.value : null)

// Create a reactive state
const selectedHeightOption = ref('empty')
const selectedCountryOption = ref('DE')
const birthdayDate = ref()
const uploadedImages = ref([])
const isValidatingForm = ref(false)
const startImageUploadValidation = ref(false)
const startBirthdayDateValidation = ref(false)
const startLastSeenTimeValidation = ref(false)
const lastSeenTime = ref(savedFormState.value?.lastSeenTime)
const mapsData = ref()

// Init form
const { values, handleSubmit, isSubmitting, errors } = useForm()

// Define pending state
const isPending = computed(() => isSubmitting.value)

// Define config objects for form fields
const firstNameConfig = computed(() => {
    return {
        label: t('form.form_firstname_label'),
        fieldType: 'text',
        id: 'firstname',
        name: 'firstname',
        placeholder: t('form.form_firstname_placeholder'),
        required: 'required',
        validationRules: yup.string()
            .max(100, t('form.form_max_characters_error_message'))
            .required(t('form.form_firstname_error_message')),
        pending: isPending.value
    }
})

const lastNameConfig = computed(() => {
    return {
        label: t('form.form_lastname_label'),
        fieldType: 'text',
        id: 'lastname',
        name: 'lastname',
        placeholder: t('form.form_lastname_placeholder'),
        required: 'required',
        validationRules: yup.string()
            .max(100, t('form.form_max_characters_error_message'))
            .required(t('form.form_lastname_error_message')),
        pending: isPending.value
    }
})

const callNameConfig = computed(() => {
    return {
        label: t('form.form_callname_label'),
        fieldType: 'text',
        id: 'callname',
        name: 'callname',
        placeholder: t('form.form_callname_placeholder'),
        validationRules: yup.string()
            .max(100, t('form.form_max_characters_error_message')),
        pending: isPending.value
    }
})

const genderConfig = computed(() => {
    return {
        female: {
            label: t('form.form_gender_female_label'),
            name: 'gender',
            uid: 'female',
            value: 'female',
            val: 'female'
        },
        male: {
            label: t('form.form_gender_male_label'),
            name: 'gender',
            value: 'male',
            uid: 'male',
            val: 'female'
        },
        diverse: {
            label: t('form.form_gender_diverse_label'),
            name: 'gender',
            value: 'diverse',
            uid: 'diverse',
            val: 'female'
        }
    }
})

const imageConfig = computed(() => {
    return {
        startValidation: startImageUploadValidation.value
    }
})

const birthdayDateConfig = computed(() => {
    return {
        locale: locale.value,
        required: 'required',
        name: 'born',
        startValidation: startBirthdayDateValidation.value,
        state: !!birthdayDate.value, // Boolean
        maxDate: new Date(),
        label: t('form.form_birthday_date_label'),
        placeholder: t('form.form_birthday_date_placeholder'),
        helperText: t('form.form_birthday_date_helper_text'),
        timePicker: false,
        errorMessage: t('form.form_birthday_date_error_message'),
        uid: 'born-datepicker'
    }
})

const getNationalityConfig = computed(() => {
    return {
        options: countries.value,
        name: 'nationality',
        id: 'nationality',
        label: t('form.form_nationality_label'),
    }
})

const eyeColorConfig = computed(() => {
    return {
        label: t('form.form_eyeColor_label'),
        fieldType: 'text',
        id: 'eyeColor',
        name: 'eyeColor',
        placeholder: t('form.form_eyeColor_placeholder'),
        validationRules: yup.string()
            .max(100, t('form.form_max_characters_error_message')),
        pending: isPending.value
    }
})

const hairColorConfig = computed(() => {
    return {
        label: t('form.form_hairColor_label'),
        fieldType: 'text',
        id: 'hairColor',
        name: 'hairColor',
        placeholder: t('form.form_hairColor_placeholder'),
        validationRules: yup.string()
            .max(100, t('form.form_max_characters_error_message')),
        pending: isPending.value
    }
})

const heightConfig = computed(() => {
    return {
        label: t('form.form_height_label'),
        fieldType: 'text',
        id: 'height',
        name: 'height',
        placeholder: t('form.form_height_placeholder'),
        validationRules: yup.string()
            .matches(/^\d*$/, t('form.form_height_error_message') || 'Only positive integers are allowed')
            .max(100, t('form.form_max_characters_error_message')),
        pending: isPending.value
    }
})

const statureConfig = computed(() => {
    return {
        disabled: stature.value?.disabled,
        options: stature.value,
        name: 'stature',
        id: 'stature',
        label: t('form.form_stature_label')
    }
})

const descriptionConfig = computed(() => {
    return {
        label: t('form.form_description_label'),
        placeholder: t('form.form_description_placeholder'),
        helperText: t('form.form_description_helper_text'),
        name: 'description',
        maxLength: 120,
        height: 100,
        id: 'description',
        pending: isPending.value,
        validationRules: yup.string()
            .max(120, t('form.form_max_characters_error_message'))
    }
})

const causeConfig = computed(() => {
    return {
        val: savedFormState.value?.cause,
        label: t('form.form_cause_label'),
        placeholder: t('form.form_cause_placeholder'),
        name: 'cause',
        id: 'cause',
        maxLength: 500,
        pending: isPending.value,
        validationRules: yup.string()
            .max(65000, t('form.form_max_characters_error_message'))
    }
})

const healthConfig = computed(() => {
    return {
        label: t('form.form_health_label'),
        placeholder: t('form.form_health_placeholder'),
        name: 'health',
        id: 'health',
        maxLength: 500,
        pending: isPending.value,
        validationRules: yup.string()
            .max(65000, t('form.form_max_characters_error_message'))
    }
})

const infosConfig = computed(() => {
    return {
        val: savedFormState.value?.cause,
        label: t('form.form_infos_label'),
        placeholder: t('form.form_infos_placeholder'),
        name: 'infos',
        id: 'infos',
        maxLength: 500,
        pending: isPending.value,
        validationRules: yup.string()
            .max(65000, t('form.form_max_characters_error_message'))
    }
})

const lastSeenTimeConfig = computed(() => {
    return {
        locale: locale.value,
        startValidation: startLastSeenTimeValidation.value,
        state: !!lastSeenTime.value, // Boolean
        name: 'lastSeenTime',
        required: 'required',
        maxDate: new Date(),
        label: t('form.form_last_seen_time_label'),
        placeholder: t('form.form_last_seen_time_placeholder'),
        helperText: t('form.form_last_seen_time_helper_text'),
        errorMessage: t('form.form_last_seen_time_error_message'),
        timePicker: true,
        uid: 'last-seen-time-datepicker'
    }
})

const lastSeenNameConfig = computed(() => {
    return {
        val: savedFormState.value?.lastSeenName,
        label: t('form.form_last_seen_name_label'),
        fieldType: 'text',
        id: 'lastSeenName',
        name: 'lastSeenName',
        placeholder: t('form.form_last_seen_name_placeholder'),
        helperText: t('form.form_last_seen_name_helperText'),
        pending: isPending.value,
        maxLength: 50,
        required: 'required',
        validationRules: yup.string()
            .max(50, t('form.form_max_characters_error_message'))
            .required(t('form.form_last_seen_name_error_message')),
    }
})

// If a user has a last seen address saved, set it as default
const savedPositionConfig = computed(() => {
    return { savedPosition: savedFormState.value?.lastSeenAddress?.position }
})

// If a user has a geometry saved, set it as default
const savedGeometryConfig = computed(() => {
    return { savedGeometry: savedFormState.value?.lastSeenGeometry?.coordinates }
})

const getGoogleMapsConfig = computed(() => {
    return {
        ...savedPositionConfig.value,
        ...savedGeometryConfig.value,
        setMarkerConfig: {
            fieldType: 'text',
            id: 'setMarkerConfig',
            name: 'setMarkerConfig',
            lightMode: true,
            placeholder: t('form.form_last_seen_address_placeholder'),
            resetInputFieldAction: {
                label: t('form.form_last_seen_address_reset_action_label'),
            }
        },
        setPolygonConfig: {
            label: t('form.form_last_seen_geometry_label'),
            id: 'lastSeenGeometry',
            name: 'lastSeenGeometry',
            readonly: true,
            placeholder: t('form.form_last_seen_geometry_placeholder'),
        }
    }
})

const reporterFirstNameConfig = computed(() => {
    return {
        val: savedFormState.value?.reporterFirstname,
        label: t('form.form_reporter_firstname_label'),
        fieldType: 'text',
        id: 'reporterFirstname',
        name: 'reporterFirstname',
        placeholder: t('form.form_reporter_firstname_placeholder'),
        required: 'required',
        validationRules: yup.string()
            .max(100, t('form.form_max_characters_error_message'))
            .required(t('form.form_reporter_firstname_error_message')),
        pending: isPending.value
    }
})

const reporterLastNameConfig = computed(() => {
    return {
        val: savedFormState.value?.reporterLastname,
        label: t('form.form_reporter_lastname_label'),
        fieldType: 'text',
        id: 'reporterLastname',
        name: 'reporterLastname',
        placeholder: t('form.form_reporter_lastname_placeholder'),
        required: 'required',
        validationRules: yup.string()
            .max(100, t('form.form_max_characters_error_message'))
            .required(t('form.form_reporter_lastname_error_message')),
        pending: isPending.value
    }
})

const reporterEmailConfig = computed(() => {
    return {
        val: savedFormState.value?.reporterEmail,
        label: t('form.form_reporter_email_label'),
        placeholder: t('form.form_reporter_email_placeholder'),
        fieldType: 'email',
        required: 'required',
        id: 'reporterEmail',
        name: 'reporterEmail',
        validationRules: yup.string()
            .max(100, t('form.form_max_characters_error_message'))
            .email(t('form.form_email_error_message'))
            .required(t('form.form_email_error_message'))
            .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, t('form.form_email_error_message')),
        pending: isPending.value
    }
})

const reporterPhoneConfig = computed(() => {
    return {
        val: savedFormState.value?.reporterPhone,
        label: t('form.form_reporter_phone_label'),
        placeholder: t('form.form_reporter_phone_placeholder'),
        fieldType: 'text',
        required: 'required',
        name: 'reporterPhone',
        id: 'reporterPhone',
        validationRules: yup.string()
            .max(100, t('form.form_max_characters_error_message'))
            .required(t('form.form_phone_error_message')),
        pending: isPending.value
    }
})

const getConsentConfig1 = computed(() => {
    return {
        val: savedFormState.value?.consents?.length > 0 ? [savedFormState.value?.consents[0]] : [],
        value: 'consent1',
        uid: 'consent1',
        label: t('form.form_consent_1_label'),
        name: 'consent1',
        validationRules: yup.array().min(1, t('form.form_consent_1_error_message')).required()
    }
})

const getConsentConfig2 = computed(() => {
    return {
        val: savedFormState.value?.consents?.length > 0 ? [savedFormState.value?.consents[1]] : [],
        value: 'consent2',
        uid: 'consent2',
        label: t('form.form_consent_2_label'),
        name: 'consent2',
        validationRules: yup.array().min(1, t('form.form_consent_2_error_message')).required()
    }
})

const getConsentConfig3 = computed(() => {
    return {
        val: savedFormState.value?.consents?.length > 0 ? [savedFormState.value?.consents[2]] : [],
        value: 'consent3',
        uid: 'consent3',
        label: t('form.form_consent_3_label'),
        link: t('form.form_consent_3_label_link'),
        name: 'consent3',
        validationRules: yup.array().min(1, t('form.form_consent_3_error_message')).required()
    }
})

const policeConfig = computed(() => {
    return {
        yes: {
            label: t('form.form_police_yes_label'),
            name: 'police',
            uid: 'yes',
            required: 'required',
            value: 'yes',
            val: savedFormState.value?.police,
            validationRules: yup
                .string()
                .oneOf(['yes', 'no'], t('form.form_police_error_message')) // Ensures the value is 'yes' or 'no'
                .required(t('form.form_police_error_message')) // Ensures a value is selected
        },
        no: {
            label: t('form.form_police_no_label'),
            name: 'police',
            uid: 'no',
            value: 'no',
            val: savedFormState.value?.police,
            conditionalText: t('form.form_police_note'),
            required: 'required',
            validationRules: yup
                .string()
                .oneOf(['yes', 'no'], t('form.form_police_error_message'))
                .required(t('form.form_police_error_message'))
        },
    }
})

const getDisclaimerConfig = computed(() => {
    return {
        val: savedFormState.value?.consents?.length > 0 ? [savedFormState.value?.consents[3]] : [],
        value: 'disclaimer',
        uid: 'disclaimer',
        label: t('form.form_disclaimer_label'),
        name: 'disclaimer',
        validationRules: yup.array().min(1, t('form.form_disclaimer_error_message')).required()
    }
})

const getConsents = computed(() => {
    if (values?.consent1 && values?.consent2 && values?.consent3 && values?.disclaimer) {
        return [...values.consent1, ...values.consent2, ...values.consent3, ...values.disclaimer]
    }

    return []
})

const submitButtonConfig = computed(() => {
    return {
        label: t('form.form_submit_label'),
        buttonConfig: {
            type: 'submit',
            buttonType: 'primary',
            large: true,
            active: true,
            tracking: [
                { track: 'click' },
                { 'track-position': 1 },
                { 'track-name': t('form.form_submit_label') },
                { 'track-url': route.fullPath }
            ]
        }
    }
})

const getFormClass = computed(() => {
    return {
        'is-submitting': isPending.value
    }
})

const formattedBirthday = computed (() => {
    if (!birthdayDate.value) return

    const date = new Date(birthdayDate.value)

    // Extract year, month, and day
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0') // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0')

    // Format as YYYY-MM-DD
    return `${year}-${month}-${day}`
})

// Compute body
const rawBody = computed(() => {
    return {
        firstname: values?.firstname,
        lastname: values?.lastname,
        callname: values?.callname,
        born: formattedBirthday?.value,
        gender: values?.gender,
        nationality: selectedCountryOption.value,
        imageBase64: uploadedImages.value?.length > 0 ? uploadedImages.value : [],
        height: values?.height,
        eyeColor: values?.eyeColor,
        hairColor: values?.hairColor,
        stature: values?.stature,
        description: values?.description,
        cause: values?.cause,
        health: values?.health,
        infos: values?.infos,
        lastSeenTime: lastSeenTime.value,
        lastSeenName: values?.lastSeenName,
        ...mapsData.value,
        reporterFirstname: values?.reporterFirstname,
        reporterLastname: values?.reporterLastname,
        reporterEmail: values?.reporterEmail,
        reporterPhone: values?.reporterPhone,
        state: 'MISSING',
        createdAt: new Date(),
        consents: getConsents.value,
        police: values?.police,
    }
})

const body = computed(() => JSON.stringify(rawBody.value))

/**
 * Start form validation
 */
const startFormValidation = () => {
    isValidatingForm.value = true
}

/**
 *  Scroll to first error
 */
const scrollToError = () => {
    const firstErrorMessage = document.querySelectorAll('.form-error')[0]

    if (firstErrorMessage) {
        const offset = 140 // Offset in pixels
        const elementPosition = firstErrorMessage.getBoundingClientRect().top + window.scrollY
        const offsetPosition = elementPosition - offset

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        })

        isValidatingForm.value = false
    }
}

// Fetch function
const { error, execute } = useFetch('/api/missing', {
    method: 'POST',
    body,
    immediate: false,
    watch: false,
    onResponse: async (res) => {
        if (res.response.status === 200) {
            await navigateTo(localePath({
                name: 'confirmation',
            }))
        } else {
            errorAlert.value = {
                type: 'form',
                label: t('form.form_error'),
            }
        }
    }
})

/**
 * Submit the form
 */
const onSubmit = handleSubmit(async () => {
    // Do some manual validation here
    startBirthdayDateValidation.value = true
    startLastSeenTimeValidation.value = true
    startImageUploadValidation.value = true

    if (!birthdayDate.value || !lastSeenTime.value || uploadedImages.value.length < 1) {
        scrollToError()
        return false
    }

    try {
        // Reset error
        errorAlert.value = null

        // Save form state
        formState.value = rawBody.value

        // Scroll to top
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        })

        // Submit the form
        await execute()
    } catch (error) {
        console.log('Form submission error:', error)

        errorAlert.value = {
            type: 'form',
            label: t('form.form_error'),
        }
    }
})

// Watch errors from useForm
watch(() => [errors.value], (newErrors, oldErrors) => {
    nextTick(() => {
        if (newErrors !== oldErrors && isValidatingForm.value) {
            if (Object.keys(errors.value).length > 0) {
                // Start validation of date picker
                startBirthdayDateValidation.value = true
                startLastSeenTimeValidation.value = true
                startImageUploadValidation.value = true

                scrollToError()
            }
        }
    })
})

// Watch error from useFetch
watch(() => [error], () => {
    if (error.value) {
        errorAlert.value = {
            type: 'form',
            label: t('form.form_error'),
        }
    }
})

// Start validation of date picker after user has interacted with the form
watch(() => lastSeenTime.value, () => {
    startLastSeenTimeValidation.value = true
})

watch(() => birthdayDate.value, () => {
    startBirthdayDateValidation.value = true
})

// Start watcher image upload
watch(() => uploadedImages.value, () => {
    startImageUploadValidation.value = true
})

// Check if user has submitted the form and clicked the restart button
watch(() => route, () => {
    if (route.query?.restart === 'true') {
        // If user adds additional form, set last seen time as default
        lastSeenTime.value = savedFormState.value?.lastSeenTime
    } else {
        formState.value = null
    }
}, { immediate: true })

const sectionTrackingAttributes = computed(() => {
    return [
        [
            { track: 'view' },
            { 'track-position': 1 },
            { section: 'form-personal-data-child', },
            { 'track-name': t('form.form_personal_data_headline') },
            { 'track-url': route.fullPath }
        ],
        [
            { track: 'view' },
            { 'track-position': 2 },
            { section: 'form-additional-information', },
            { 'track-name': t('form.form_additional_information_headline') },
            { 'track-url': route.fullPath }
        ],
        [
            { track: 'view' },
            { 'track-position': 3 },
            { section: 'form-map', },
            { 'track-name': t('form.form_maps_headline') },
            { 'track-url': route.fullPath }
        ],
        [
            { track: 'view' },
            { 'track-position': 4 },
            { section: 'form-contact-data', },
            { 'track-name': t('form.form_contact_data_headline') },
            { 'track-url': route.fullPath }
        ],
        [
            { track: 'view' },
            { 'track-position': 5 },
            { section: 'form-police', },
            { 'track-name': t('form.form_police_headline') },
            { 'track-url': route.fullPath }
        ],
        [
            { track: 'view' },
            { 'track-position': 6 },
            { section: 'form-consent', },
            { 'track-name': t('form.form_consent_headline') },
            { 'track-url': route.fullPath }
        ],
        [
            { track: 'view' },
            { 'track-position': 7 },
            { section: 'form-disclaimer', },
            { 'track-name': t('form.form_disclaimer_headline') },
            { 'track-url': route.fullPath }
        ],

    ]
})

const sectionTracking1 = computed(() => {
    if (!sectionTrackingAttributes.value) return {}

    const attributes = {}

    sectionTrackingAttributes.value[0].forEach((obj) => {
        for (const [key, value] of Object.entries(obj)) {
            attributes[`data-${key}`] = value
        }
    })
    return attributes
})

const sectionTracking2 = computed(() => {
    if (!sectionTrackingAttributes.value) return {}

    const attributes = {}

    sectionTrackingAttributes.value[1].forEach((obj) => {
        for (const [key, value] of Object.entries(obj)) {
            attributes[`data-${key}`] = value
        }
    })
    return attributes
})

const sectionTracking3 = computed(() => {
    if (!sectionTrackingAttributes.value) return {}

    const attributes = {}

    sectionTrackingAttributes.value[2].forEach((obj) => {
        for (const [key, value] of Object.entries(obj)) {
            attributes[`data-${key}`] = value
        }
    })
    return attributes
})

const sectionTracking4 = computed(() => {
    if (!sectionTrackingAttributes.value) return {}

    const attributes = {}

    sectionTrackingAttributes.value[3].forEach((obj) => {
        for (const [key, value] of Object.entries(obj)) {
            attributes[`data-${key}`] = value
        }
    })
    return attributes
})

const sectionTracking5 = computed(() => {
    if (!sectionTrackingAttributes.value) return {}

    const attributes = {}

    sectionTrackingAttributes.value[4].forEach((obj) => {
        for (const [key, value] of Object.entries(obj)) {
            attributes[`data-${key}`] = value
        }
    })
    return attributes
})

const sectionTracking6 = computed(() => {
    if (!sectionTrackingAttributes.value) return {}

    const attributes = {}

    sectionTrackingAttributes.value[5].forEach((obj) => {
        for (const [key, value] of Object.entries(obj)) {
            attributes[`data-${key}`] = value
        }
    })
    return attributes
})

const sectionTracking7 = computed(() => {
    if (!sectionTrackingAttributes.value) return {}

    const attributes = {}

    sectionTrackingAttributes.value[6].forEach((obj) => {
        for (const [key, value] of Object.entries(obj)) {
            attributes[`data-${key}`] = value
        }
    })
    return attributes
})

// Compute progress
const requiredFields = computed(() => {
    return {
        firstname: rawBody.value?.firstname,
        lastname: rawBody.value?.lastname,
        // gender: rawBody.value?.gender,
        // nationality: rawBody.value?.nationality,
        born: rawBody.value?.born,
        imageBase64: rawBody.value?.imageBase64,
        lastSeenTime: rawBody.value?.lastSeenTime,
        lastSeenName: rawBody.value?.lastSeenName,
        reporterFirstname: rawBody.value?.reporterFirstname,
        reporterLastname: rawBody.value?.reporterLastname,
        reporterEmail: rawBody.value?.reporterEmail,
        reporterPhone: rawBody.value?.reporterPhone,
        consents: rawBody.value?.consents,
        police: rawBody.value?.police
    }
})

const fieldProgress = computed(() => {
    const requiredFieldsCount = Object.values(requiredFields.value).filter(value =>
        value && !(Array.isArray(value) && value.length === 0)
    ).length

    const consentsCount = requiredFields.value.consents?.length > 0 ? requiredFields.value.consents.length : 0

    return requiredFieldsCount + consentsCount
})

const requiredFieldsCount = computed(() => Object.keys(requiredFields.value).length + requiredFields.value.consents?.length)
const singleStep = computed(() => 100 / requiredFieldsCount.value)
const progress = computed(() => `${singleStep.value * fieldProgress.value}%`)
</script>

<template>
    <section class="missing-children-form-section">
        <form
            :class="getFormClass"
            class="missing-children-form"
            novalidate
            autocomplete="off"
            @submit.prevent="onSubmit"
        >
            <LoadingSpinner v-if="isPending" class="fullscreen-loader" />

            <MainHeadline :main-headline-config="{ title: t('form.form_main_headline') }" />

            <FormSection v-bind="sectionTracking1">
                <FormHeadline :form-headline-config="{ title: t('form.form_personal_data_headline') }" />
                <FormContainer :form-container-config="{ sections: 2 }">
                    <FormContainer :form-container-config="{ sections: 1 }">
                        <FormText :input-config="firstNameConfig" />
                        <FormText :input-config="lastNameConfig" />
                        <FormText :input-config="callNameConfig" />

                        <div>
                            <FormLabelStandalone>
                                {{ t('form.form_gender_label') }}:
                            </FormLabelStandalone>

                            <FormRadioGroup>
                                <FormRadio :radio-button-config="genderConfig?.female" />
                                <FormRadio :radio-button-config="genderConfig?.male" />
                                <FormRadio :radio-button-config="genderConfig?.diverse" />
                            </FormRadioGroup>
                        </div>

                        <FormDatePicker v-model="birthdayDate" :date-picker-config="birthdayDateConfig" />

                        <FormSelect v-model="selectedCountryOption" :form-select-config="getNationalityConfig" />
                    </FormContainer>

                    <FormImageUpload v-model="uploadedImages" :image-config="imageConfig" />
                </FormContainer>

                <FormSection v-bind="sectionTracking2">
                    <ToggleItem>
                        <template #title>
                            <FormHeadline :form-headline-config="{ title: t('form.form_child_description_headline') }" />
                        </template>

                        <template #content="{ isOpen }">
                            <FormContainer :form-container-config="{ sections: 2 }">
                                <FormText :input-config="{ ...eyeColorConfig, disabled: !isOpen }" />
                                <FormText :input-config="{ ...hairColorConfig, disabled: !isOpen }" />
                                <FormText :input-config="{ ...heightConfig, disabled: !isOpen }" />
                                <FormSelect
                                    v-model="selectedHeightOption"
                                    :form-select-config="{ ...statureConfig, disabled: !isOpen }"
                                />
                            </FormContainer>

                            <FormContainer :form-container-config="{ sections: 1 }">
                                <FormTextarea :textarea-config="{ ...descriptionConfig, disabled: !isOpen }" />
                            </FormContainer>
                        </template>
                    </ToggleItem>
                </FormSection>
            </FormSection>

            <FormSection v-bind="sectionTracking2">
                <ToggleItem>
                    <template #title>
                        <FormHeadline :form-headline-config="{ title: t('form.form_additional_information_headline') }" />
                    </template>

                    <template #content="{ isOpen }">
                        <FormContainer :form-container-config="{ sections: 1 }">
                            <FormTextarea :textarea-config="{ ...healthConfig, disabled: !isOpen }" />
                            <FormTextarea :textarea-config="{ ...causeConfig, disabled: !isOpen }" />
                            <FormTextarea :textarea-config="{ ...infosConfig, disabled: !isOpen }" />
                        </FormContainer>
                    </template>
                </ToggleItem>
            </FormSection>

            <FormSection v-bind="sectionTracking3">
                <FormHeadline :form-headline-config="{ title: t('form.form_maps_headline') }" />
                <FormContainer :form-container-config="{ sections: 2 }">
                    <FormDatePicker v-model="lastSeenTime" :date-picker-config="lastSeenTimeConfig" />
                    <FormText :input-config="lastSeenNameConfig" />
                </FormContainer>

                <p class="form-text" v-html="t('form.form_maps_intro')" />
                <ClientOnly>
                    
                    <GoogleMap v-model="mapsData" :google-maps-config="getGoogleMapsConfig" />
                </ClientOnly>
            </FormSection>

            <FormSection v-bind="sectionTracking4">
                <FormHeadline :form-headline-config="{ title: t('form.form_contact_data_headline') }" />

                <FormContainer :form-container-config="{ sections: 2 }">
                    <FormText :input-config="reporterFirstNameConfig" />
                    <FormText :input-config="reporterLastNameConfig" />
                </FormContainer>

                <FormContainer :form-container-config="{ sections: 2 }">
                    <FormText :input-config="reporterEmailConfig" />
                    <FormText :input-config="reporterPhoneConfig" />
                </FormContainer>
            </FormSection>

            <FormSection v-bind="sectionTracking5">
                <FormHeadline :form-headline-config="{ title: t('form.form_police_headline') }" />

                <FormRadioGroup>
                    <FormRadio :radio-button-config="policeConfig?.yes" />
                    <FormRadio :radio-button-config="policeConfig?.no" />
                </FormRadioGroup>
            </FormSection>

            <FormSection v-bind="sectionTracking6">
                <FormHeadline :form-headline-config="{ title: t('form.form_consent_headline') }" />
                <FormCheckBoxGroup>
                    <FormCheckBox :checkbox-config="getConsentConfig1" />
                    <FormCheckBox :checkbox-config="getConsentConfig2" />
                    <FormCheckBox :checkbox-config="getConsentConfig3" />
                </FormCheckBoxGroup>
            </FormSection>

            <FormSection v-bind="sectionTracking7">
                <FormHeadline :form-headline-config="{ title: t('form.form_disclaimer_headline') }" />
                <FormCheckBoxGroup>
                    <FormCheckBox :checkbox-config="getDisclaimerConfig" />
                </FormCheckBoxGroup>
            </FormSection>

            <p class="asterisk-note" v-html="t('form.form_asterisk')" />

            <FormButton :form-button-config="submitButtonConfig" @click="startFormValidation" />
        </form>
    </section>

    <Teleport to="body">
        <div
            class="progress-bar"
            role="progressbar"
            :aria-valuenow="progress"
            :aria-valuemin="0"
            :aria-valuemax="100"
            aria-live="polite"
        />
    </Teleport>
</template>

<style lang="scss">
.missing-children-form-section {
grid-column: 2 / -2;

@include section-padding($first: true);
}

.form-text {
line-height: 1.7;
}

.missing-children-form {
&.is-submitting {
   pointer-events: none;

   &::after {
       display: block;
   }
}

&::after {
   content: '';
   background-color: $form-is-submitting-bg;
   position: fixed;
   inset: 0;
   display: none;
}

h1 {
   text-align: center;
}
}

.asterisk-note {
position: relative;
font-size: px2rem(14);

@include bp(medium1) {
   font-size: px2rem(18);
}

span {
   position: absolute;
   top: px2rem(15);
   left: px2rem(-20);
}
}

.progress-bar {
position: fixed;
top: 0;
height: px2rem(5);
z-index: z('progress-bar');
background: $progress-bar;
transition: width 0.5s ease-in-out;
width: v-bind(progress);
}
</style>
